import React, { useState, useEffect, useRef } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  MessageCircle, Search, Trash2, Edit, MapPin, 
  Calendar, User, Heart, Save, RefreshCw, 
  AlertTriangle, Eye, ExternalLink, Loader2
} from 'lucide-react';

export default function AdminComments() {
  const { adminAPI } = useAdmin();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [selectedComment, setSelectedComment] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalComments, setTotalComments] = useState(0);
  const [stats, setStats] = useState(null);

  const observerRef = useRef();
  const loadMoreRef = useRef();
  const commentsPerPage = 20;

  // Reset quand les filtres changent
  useEffect(() => {
    setComments([]);
    setHasMore(true);
    loadComments(true);
    loadStats();
  }, [searchTerm, authorFilter]);

  // Intersection Observer pour scroll infini
  useEffect(() => {
    const options = { root: null, rootMargin: '200px', threshold: 0.1 };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreComments();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loadingMore, loading, comments.length]);

  const loadComments = async (reset = false) => {
    try {
      setLoading(true);
      const params = {
        skip: 0,
        limit: commentsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(authorFilter && { author: authorFilter })
      };
      
      const data = await adminAPI.getComments(params);
      setComments(data.comments || []);
      setTotalComments(data.total || 0);
      setHasMore((data.comments || []).length === commentsPerPage);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreComments = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const params = {
        skip: comments.length,
        limit: commentsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(authorFilter && { author: authorFilter })
      };
      
      const data = await adminAPI.getComments(params);
      const newComments = data.comments || [];
      setComments(prev => [...prev, ...newComments]);
      setHasMore(newComments.length === commentsPerPage);
    } catch (error) {
      console.error('Failed to load more comments:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminAPI.getCommentsStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load comment stats:', error);
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ce commentaire de "${comment.author}" ?`)) {
      return;
    }
    
    try {
      setActionLoading(true);
      await adminAPI.deleteComment(comment.spot_id, comment.comment_index);
      await loadComments();
      await loadStats();
      setSelectedComment(null);
    } catch (error) {
      console.error('Failed to delete comment:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateComment = async (comment, updateData) => {
    try {
      setActionLoading(true);
      await adminAPI.updateComment(comment.spot_id, comment.comment_index, updateData);
      await loadComments();
      setEditingComment(null);
    } catch (error) {
      console.error('Failed to update comment:', error);
      alert('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Modal d'édition de commentaire
  const CommentEditModal = ({ comment, onSave, onClose }) => {
    const [editData, setEditData] = useState({
      author: comment.author || '',
      content: comment.content || '',
      likes: comment.likes || 0
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(comment, editData);
    };

    return (
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Modifier le commentaire
          </DialogTitle>
          <DialogDescription>
            Commentaire sur "{comment.spot_title}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Auteur</Label>
            <Input
              value={editData.author}
              onChange={(e) => setEditData(prev => ({...prev, author: e.target.value}))}
              placeholder="Nom de l'auteur"
            />
          </div>

          <div>
            <Label>Contenu du commentaire</Label>
            <Textarea
              value={editData.content}
              onChange={(e) => setEditData(prev => ({...prev, content: e.target.value}))}
              rows={4}
              placeholder="Contenu du commentaire..."
            />
          </div>

          <div>
            <Label>Nombre de likes</Label>
            <Input
              type="number"
              min="0"
              value={editData.likes}
              onChange={(e) => setEditData(prev => ({...prev, likes: parseInt(e.target.value) || 0}))}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={actionLoading} className="flex-1">
              <Save className="h-4 w-4 mr-2" />
              {actionLoading ? 'Enregistrement...' : 'Sauvegarder'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Commentaires</h1>
          <p className="text-gray-600 mt-1">Modérer et gérer tous les commentaires de la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-600">{totalComments} commentaires</span>
        </div>
      </div>

      {/* Stats rapides */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <MessageCircle className="h-6 w-6 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{stats.total_comments}</p>
              <p className="text-sm text-gray-500">Commentaires</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Heart className="h-6 w-6 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold">{stats.total_likes}</p>
              <p className="text-sm text-gray-500">Likes totaux</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <User className="h-6 w-6 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold">{stats.unique_authors}</p>
              <p className="text-sm text-gray-500">Auteurs uniques</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Heart className="h-6 w-6 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{stats.avg_likes_per_comment}</p>
              <p className="text-sm text-gray-500">Moy. likes/comm.</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher dans les commentaires..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Input
                placeholder="Filtrer par auteur"
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={() => { loadComments(); loadStats(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des commentaires */}
      <Card>
        <CardHeader>
          <CardTitle>Tous les Commentaires</CardTitle>
          <CardDescription>
            Liste de tous les commentaires avec leur contexte
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement des commentaires...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div 
                    key={comment.id}
                    className="border border-gray-200 rounded-lg hover:border-blue-300 transition-colors overflow-hidden"
                  >
                    {/* Header du commentaire avec contexte du spot */}
                    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
                      <div className="flex items-center gap-3">
                        {/* Miniature du spot */}
                        {comment.spot_photo && (
                          <img 
                            src={comment.spot_photo}
                            alt=""
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-sm">{comment.spot_title}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            Spot par {comment.spot_author}
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        ID: {comment.spot_id.slice(0, 8)}...
                      </Badge>
                    </div>

                    {/* Corps du commentaire */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Info auteur */}
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <span className="font-medium">{comment.author}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                {formatDate(comment.timestamp)}
                              </span>
                            </div>
                          </div>

                          {/* Contenu */}
                          <p className="text-gray-700 text-sm leading-relaxed mb-3">
                            {comment.content}
                          </p>

                          {/* Stats et actions */}
                          <div className="flex items-center gap-4">
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Heart className="h-3 w-3 text-red-500" />
                              {comment.likes} likes
                            </Badge>
                            
                            {comment.edited_by && (
                              <span className="text-xs text-orange-600">
                                ✏️ Modifié par {comment.edited_by}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setEditingComment(comment)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteComment(comment)}
                            disabled={actionLoading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {comments.length === 0 && (
                <div className="text-center py-12">
                  <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun commentaire trouvé</p>
                </div>
              )}

              {/* Scroll infini loader */}
              <div ref={loadMoreRef} className="flex justify-center py-6">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Chargement...</span>
                  </div>
                )}
                {!hasMore && comments.length > 0 && (
                  <p className="text-gray-400 text-sm">{totalComments} commentaires au total</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal d'édition */}
      <Dialog open={!!editingComment} onOpenChange={() => setEditingComment(null)}>
        {editingComment && (
          <CommentEditModal 
            comment={editingComment} 
            onSave={handleUpdateComment}
            onClose={() => setEditingComment(null)}
          />
        )}
      </Dialog>
    </div>
  );
}
